import { Badge, Typography } from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import dayjs from "dayjs";
import { FilterType, TableHeaderType } from "@/components/tables/types";
import type { EnrichedSubscriptionListItem } from "@/pages/subscriptions/services/types";
import type { SubscriptionColumnsConfig } from "./types";
import { Link } from "react-router-dom";
import { RunStatusIcon } from "./RunStatusIcon";
import { SubscriptionActionsMenu } from "./SubscriptionActionsMenu";
import { SEARCH_PARAMS, SubscriptionStatus, RunStatus } from "../../constants";
import { formatCronExpression } from "./utils/cronFormatter";
import type { RecentRun } from "@/pages/subscriptions/services/types";

export const getSubscriptionColumns = (
  config: SubscriptionColumnsConfig
): TableHeaderType<EnrichedSubscriptionListItem>[] => [
  {
    id: 0,
    uid: "name",
    label: "Name",
    width: "18%",
    render: item => (
      <Link to={`/subscriptions/${item.id}`}>
        <Typography variant="body" className="al-text-blue-600 hover:al-underline">
          {item.name}
        </Typography>
      </Link>
    ),
  },
  {
    id: 1,
    uid: "category",
    label: "Category",
    width: "14%",
    render: item => {
      const displayValue =
        item.categoryDisplayName ||
        (item.category
          ? item.category.charAt(0).toUpperCase() + item.category.slice(1)
          : item.entity_type
            ? item.entity_type.charAt(0).toUpperCase() + item.entity_type.slice(1)
            : "");
      return (
        <Typography variant="body" className="al-text-sm">
          {displayValue}
        </Typography>
      );
    },
    filter: {
      filterType: FilterType.DROPDOWN,
      options: config.categoryOptions,
      searchKey: SEARCH_PARAMS.CATEGORY,
    },
  },
  {
    id: 2,
    uid: "rule_type",
    label: "Subscription Type",
    width: "12%",
    render: item => (
      <Typography variant="body" className="al-capitalize al-text-sm">
        {item.rule_type}
      </Typography>
    ),
  },
  {
    id: 3,
    uid: "schedule",
    label: "Schedule",
    width: "14%",
    render: item => (
      <Typography variant="body" className="al-text-sm">
        {item.delivery.schedule ? formatCronExpression(item.delivery.schedule.cron) : "—"}
      </Typography>
    ),
  },
  {
    id: 4,
    uid: "last_run",
    label: "Recent Runs",
    width: "14%",
    render: item => {
      const runs = item.recent_runs?.slice(0, 5) ?? [];
      const legacyRun = item.last_run;

      if (runs.length === 0 && !legacyRun) {
        return (
          <Stack direction="row" align="center" gap="xs" className="al-flex-wrap">
            {Array.from({ length: 5 }).map((_, idx) => (
              <RunStatusIcon key={`placeholder-${idx}`} status="pending" />
            ))}
          </Stack>
        );
      }

      // Use recent_runs when available, fall back to last_run
      const normalizedRuns: RecentRun[] =
        runs.length > 0
          ? runs
          : legacyRun
            ? [
                {
                  id: legacyRun.id,
                  status: legacyRun.status,
                  executed_at: legacyRun.executed_at,
                  message: legacyRun.message ?? null,
                },
              ]
            : [];

      const MAX_DISPLAY = 5;
      const placeholderCount = Math.max(0, MAX_DISPLAY - normalizedRuns.length);

      return (
        <Stack direction="row" align="center" gap="xs" className="al-flex-wrap">
          {normalizedRuns.map((run, idx) => (
            <RunStatusIcon
              key={run.id ?? idx}
              status={run.status}
              alertTriggered={run.status === RunStatus.TRIGGERED}
            />
          ))}
          {Array.from({ length: placeholderCount }).map((_, idx) => (
            <RunStatusIcon key={`placeholder-${idx}`} status="pending" />
          ))}
        </Stack>
      );
    },
  },
  {
    id: 5,
    uid: "destination",
    label: "Destination",
    width: "12%",
    render: item => (
      <Typography variant="body" className="al-text-sm al-capitalize">
        {[...new Set(item.delivery.notifiers.map(n => n.type))].join(", ")}
      </Typography>
    ),
    filter: {
      filterType: FilterType.DROPDOWN,
      options: config.destinationOptions,
      searchKey: SEARCH_PARAMS.DESTINATION,
    },
  },
  {
    id: 7,
    uid: "created_at",
    label: "Created on",
    width: "12%",
    render: item => (
      <Typography variant="body" className="al-text-sm">
        {dayjs(item.created_at).format("DD MMM YYYY, h:mmA")}
      </Typography>
    ),
    sort: {
      sortSearchKey: SEARCH_PARAMS.SORT_BY,
      sortOrderSearchKey: SEARCH_PARAMS.SORT,
    },
  },
  {
    id: 8,
    uid: "status",
    label: "Status",
    width: "10%",
    render: item =>
      item.status === SubscriptionStatus.ACTIVE ? (
        <Badge variant="outline" className="al-bg-green-100 al-text-green-500 al-border-none">
          Active
        </Badge>
      ) : (
        <Badge variant="outline" className="al-bg-yellow-100 al-text-yellow-500 al-border-none">
          Inactive
        </Badge>
      ),
  },
  {
    id: 9,
    uid: "actions",
    label: "Actions",
    width: "7%",
    render: item => (
      <SubscriptionActionsMenu
        id={item.id}
        status={item.status}
        readOnly={item.read_only}
        onToggleStatus={config.onToggleStatus}
        onDelete={config.onDelete}
        onClone={config.onClone}
      />
    ),
  },
];
